import { TypographyOptions } from "@mui/material/styles/createTypography";
import type { CSSProperties } from "react";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    stat: CSSProperties;
  }
  interface TypographyVariantsOptions {
    stat?: CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    stat: true;
  }
}

/** One family; weight and width (`font-stretch`) carry the hierarchy. */
export const fontFamily =
  "'Archivo', system-ui, -apple-system, 'Segoe UI', sans-serif";
export const fontMono = "'JetBrains Mono', ui-monospace, Menlo, monospace";

export const fontStretch = {
  display: "125%",
  /** Drawer, dialog and card titles ("font-stretch:115%" in the design). */
  heading: "115%",
  condensed: "62%",
} as const;

/** Floors: body text 14 px, table cells 13 px, eyebrows and chips 10.5 px uppercase. */
export const typography: TypographyOptions = {
  fontFamily,
  fontSize: 14,
  htmlFontSize: 16,
  fontWeightLight: 500,
  fontWeightRegular: 500,
  fontWeightMedium: 600,
  fontWeightBold: 800,
  /** Page title: 36/900 expanded uppercase (PageHeader adds the transform). */
  h1: {
    fontSize: 36,
    fontWeight: 900,
    fontStretch: fontStretch.display,
    lineHeight: 1,
    letterSpacing: "-.01em",
  },
  /** Dialog and drawer titles. */
  h2: {
    fontSize: 24,
    fontWeight: 900,
    fontStretch: fontStretch.heading,
    lineHeight: 1.1,
  },
  h3: {
    fontSize: 22,
    fontWeight: 900,
    fontStretch: fontStretch.heading,
    lineHeight: 1.1,
  },
  /** Section headings inside cards: 16/900 expanded uppercase. */
  h4: {
    fontSize: 16,
    fontWeight: 900,
    fontStretch: fontStretch.display,
    textTransform: "uppercase",
    lineHeight: 1.2,
  },
  /** Section headings on the page: 18/900 expanded uppercase. */
  h5: {
    fontSize: 18,
    fontWeight: 900,
    fontStretch: fontStretch.display,
    textTransform: "uppercase",
    lineHeight: 1,
  },
  /** Small card headings: 15/900 expanded uppercase. */
  h6: {
    fontSize: 15,
    fontWeight: 900,
    fontStretch: fontStretch.display,
    textTransform: "uppercase",
    lineHeight: 1.2,
  },
  subtitle1: { fontSize: 15, fontWeight: 600, lineHeight: 1.45 },
  subtitle2: { fontSize: 14, fontWeight: 600, lineHeight: 1.45 },
  body1: { fontSize: 15, fontWeight: 500, lineHeight: 1.5 },
  body2: { fontSize: 14, fontWeight: 500, lineHeight: 1.5 },
  caption: { fontSize: 12.5, fontWeight: 500, lineHeight: 1.4 },
  /** Eyebrow: 11/800 expanded, .08em tracking, uppercase. */
  overline: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: ".08em",
    fontStretch: fontStretch.display,
    lineHeight: 1.4,
  },
  button: {
    fontSize: 14,
    fontWeight: 700,
    textTransform: "none",
    lineHeight: 1.5,
  },
  /** Big numerals: condensed Archivo (`var(--num)` in the design). */
  stat: {
    fontFamily,
    fontSize: 44,
    fontWeight: 800,
    fontStretch: fontStretch.condensed,
    lineHeight: 0.9,
  },
};
